import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import toastr from "toastr";

import Listbox from "../../../components/Listbox";
import { makeGETRequest, makeGETRequestOnUserService } from "../../../Api/api";
import { IUser, UserDetails } from "../../../components/Calculator/interfaces";
import {
  BondOrderHistoryResponse,
  IPOOrderHistoryResponse,
} from "../../../components/Users/Orders/helpers";
import {
  COMPANY_DETAILS,
  PAGE_MARGIN,
  buildDate,
  buildDateTime,
  buildInvoiceNumber,
  convertNumberToWords,
  ensureUuid,
  formatCurrency,
  formatDate,
  isValidDateValue,
  parseAmount,
  roundCurrency,
  sanitizeFileName,
} from "./common";
import { PlusJakartaSansFontBase64String } from "../../../Helpers/PlusJakartaSans-base64String";
import { faviconPngBase64 } from "../../../Helpers/faviconPngBase64";

const TDS_RATE = 10;

export interface ReferEarnInvoiceForm {
  documentUuid: string;
  documentDateTime: string;
  invoiceDate: string;
  invoiceNumber: string;
  rewardAmount: string;
  paymentDate: string;
  utrNumber: string;
  applyTds: boolean;
}

export interface UserOption {
  id: string;
  name: string;
}

export interface ReferEarnReferral {
  rowId: string;
  uuid?: string;
  name?: string;
  email?: string;
  mobile?: string;
  smest_id?: string;
  user_uuid?: string;
  user_signup_date?: string;
  order_date?: string;
  amount?: number;
  type?: string;
  order_uuid?: string;
  reward_claim?: boolean;
  [key: string]: any;
}

interface ReferralDetail {
  user_uuid?: string;
  referral_name?: string;
  user_signup_date?: string;
  order_date?: string;
  amount?: number;
  type?: string;
  order_uuid?: string;
  reward_claim?: boolean;
  [key: string]: any;
}

export interface ReferralSummary {
  referrals: ReferEarnReferral[];
  active_referral_users_count: number;
  invested_referral_count: number;
  reward_earned: number;
  reward_claimed: number;
}

export type OrderSelectionType = "BOND" | "IPO";

export interface ReferEarnOrderSelection {
  type: OrderSelectionType;
  id: string;
  title: string;
  subtitle?: string;
  details: BondOrderHistoryResponse | IPOOrderHistoryResponse;
  orderDate?: string;
  utrNumber?: string;
}

export interface ReferEarnState {
  selectedUser: UserOption | null;
  userDetails: UserDetails | null;
  userDetailsLoading: boolean;
  referrals: ReferEarnReferral[];
  referralSummary: ReferralSummary | null;
  referralsLoading: boolean;
  referralError?: string;
  selectedReferralId: string | null;
  bondOrders: BondOrderHistoryResponse[];
  ipoOrders: IPOOrderHistoryResponse[];
  ordersLoading: boolean;
  orderError?: string;
  selectedOrder: ReferEarnOrderSelection | null;
}

export interface ReferEarnCalculations {
  rewardAmount: number;
  tdsAmount: number;
  totalPayable: number;
  amountInWords: string;
}

export const createReferEarnInvoiceForm = (): ReferEarnInvoiceForm => ({
  documentUuid: ensureUuid(),
  documentDateTime: buildDateTime(),
  invoiceDate: buildDate(),
  invoiceNumber: buildInvoiceNumber(),
  rewardAmount: "1000",
  paymentDate: buildDate(),
  utrNumber: "",
  applyTds: false,
});

export const createReferEarnState = (): ReferEarnState => ({
  selectedUser: null,
  userDetails: null,
  userDetailsLoading: false,
  referrals: [],
  referralSummary: null,
  referralsLoading: false,
  referralError: undefined,
  selectedReferralId: null,
  bondOrders: [],
  ipoOrders: [],
  ordersLoading: false,
  orderError: undefined,
  selectedOrder: null,
});

export const calculateReferEarn = (
  form: ReferEarnInvoiceForm
): ReferEarnCalculations => {
  const baseAmount = parseAmount(form.rewardAmount);
  if (!Number.isFinite(baseAmount) || baseAmount <= 0) {
    return {
      rewardAmount: Number.NaN,
      tdsAmount: Number.NaN,
      totalPayable: Number.NaN,
      amountInWords: "",
    };
  }
  const tdsAmount = form.applyTds
    ? roundCurrency((baseAmount * TDS_RATE) / 100)
    : 0;
  const totalPayable = roundCurrency(baseAmount - tdsAmount);
  return {
    rewardAmount: baseAmount,
    tdsAmount,
    totalPayable,
    amountInWords: convertNumberToWords(totalPayable),
  };
};

export const getSelectedReferral = (
  state: ReferEarnState
): ReferEarnReferral | null =>
  state.referrals.find((item) => item.rowId === state.selectedReferralId) ||
  null;

export const getSelectedOrder = (
  state: ReferEarnState
): ReferEarnOrderSelection | null => state.selectedOrder ?? null;

export const validateReferEarnForm = (
  form: ReferEarnInvoiceForm,
  state: ReferEarnState
) => {
  const validation: Record<string, string> = {};
  if (!form.documentUuid.trim()) validation.documentUuid = "Required";
  if (!isValidDateValue(form.documentDateTime))
    validation.documentDateTime = "Invalid date";
  if (!isValidDateValue(form.invoiceDate))
    validation.invoiceDate = "Invalid date";
  if (!form.invoiceNumber.trim()) validation.invoiceNumber = "Required";

  const amount = parseAmount(form.rewardAmount);
  if (!Number.isFinite(amount) || amount <= 0) {
    validation.rewardAmount = "Enter a valid reward amount";
  }

  if (!form.paymentDate || !isValidDateValue(form.paymentDate)) {
    validation.paymentDate = "Select a valid payment date";
  }

  if (!form.utrNumber.trim()) {
    validation.utrNumber = "Enter UTR number";
  }

  if (!state.selectedUser) {
    validation.selectedUser = "Select a user";
  }
  if (!state.userDetails) {
    validation.userDetails = "Unable to fetch user details";
  }
  if (!getSelectedReferral(state)) {
    validation.selectedReferral = "Select one referral";
  }
  if (!getSelectedOrder(state)) {
    validation.selectedOrder = "Select one order";
  }

  return validation;
};

const buildReferralDescription = (
  referral: ReferEarnReferral | null,
  userDetails: UserDetails | null
) => {
  const referralName =
    referral?.name || referral?.referral_name || userDetails?.firstName || "";
  const smestId = referral?.smest_id || userDetails?.smestID || "";
  const signup = referral?.user_signup_date
    ? formatDate(referral.user_signup_date, "YYYY-MM-DD HH:mm")
    : "--";
  const orderDate = referral?.order_date
    ? formatDate(referral.order_date, "YYYY-MM-DD HH:mm")
    : "--";
  const investmentAmount =
    typeof referral?.amount === "number"
      ? formatCurrency(referral.amount)
      : "--";

  return [
    "Being Referral Fees for",
    `${referralName} ${smestId ? `(${smestId})` : ""}`.trim(),
    `Sign Up Date: ${signup}`,
    `Order Date: ${orderDate}`,
    `Investment Amount: ${investmentAmount}`,
  ]
    .map((line) => line.trim())
    .join("\n");
};

const buildUserAddressLines = (userDetails: UserDetails | null) => {
  if (!userDetails) return [];
  const lines: string[] = [];
  const fullName = `${userDetails.firstName || ""} ${
    userDetails.lastName || ""
  }`
    .trim()
    .replace(/\s+/g, " ");
  if (fullName) lines.push(fullName);
  const address = userDetails.address?.split("\n") ?? [];
  lines.push(...address.filter(Boolean));
  const cityLine = [userDetails.city, userDetails.state, userDetails.pincode]
    .filter(Boolean)
    .join(", ");
  if (cityLine) lines.push(cityLine);
  return lines.filter(Boolean);
};

const extractOrderIdentifier = (
  type: OrderSelectionType,
  order: BondOrderHistoryResponse | IPOOrderHistoryResponse
) => {
  if (type === "BOND") {
    return (
      (order as BondOrderHistoryResponse).uuid ||
      (order as BondOrderHistoryResponse).rfq_deal_id ||
      (order as BondOrderHistoryResponse).rfq_order_number ||
      `${(order as BondOrderHistoryResponse).security_name}-${
        (order as BondOrderHistoryResponse).insertedAt
      }`
    );
  }
  const ipo = order as IPOOrderHistoryResponse;
  return (
    ipo.uuid ||
    ipo.applicationno ||
    ipo.bankreferenceno ||
    `${ipo.scripid}-${ipo.insertedAt}`
  );
};

const extractOrderTitle = (
  type: OrderSelectionType,
  order: BondOrderHistoryResponse | IPOOrderHistoryResponse
) => {
  if (type === "BOND") {
    const bond = order as BondOrderHistoryResponse;
    return bond.security_name || "Bond Order";
  }
  const ipo = order as IPOOrderHistoryResponse;
  return (
    ipo.ipo_security_name ||
    ipo.issuer_name ||
    `IPO Order ${ipo.applicationno || ""}`.trim()
  );
};

const extractOrderSubtitle = (
  type: OrderSelectionType,
  order: BondOrderHistoryResponse | IPOOrderHistoryResponse
) => {
  if (type === "BOND") {
    const bond = order as BondOrderHistoryResponse;
    const status = bond.deal_status || bond.order_status || "Status NA";
    const dateSource =
      bond.insertedAt || bond.updatedAt || bond.settlement_date;
    const formattedDate = dateSource
      ? moment(dateSource).format("DD MMM YYYY")
      : "Date NA";
    return `${status} • ${formattedDate}`;
  }
  const ipo = order as IPOOrderHistoryResponse;
  const status = ipo.allotmentstatus || ipo.sponsorbankpaystatus || "Status NA";
  const formattedDate = ipo.order_placed
    ? moment(ipo.order_placed).format("DD MMM YYYY")
    : "Date NA";
  return `${status} • ${formattedDate}`;
};

const extractOrderDate = (
  type: OrderSelectionType,
  order: BondOrderHistoryResponse | IPOOrderHistoryResponse
) => {
  if (type === "BOND") {
    const bond = order as BondOrderHistoryResponse;
    return (
      bond.settlement_date || bond.insertedAt || bond.updatedAt || undefined
    );
  }
  const ipo = order as IPOOrderHistoryResponse;
  return ipo.order_placed || ipo.sponsorbankactiontime || undefined;
};

const extractOrderUtr = (
  order: BondOrderHistoryResponse | IPOOrderHistoryResponse
) => {
  const candidates = [
    "utr",
    "utrNumber",
    "utr_number",
    "bankreferenceno",
    "bankreferencechequeno",
    "payment_reference_no",
    "fund_transfer_utr",
    "fund_transfer_utr_number",
    "reference_number",
    "bank_reference_no",
  ];
  for (const key of candidates) {
    const value = (order as any)[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const buildReferralRowId = (
  referral: ReferEarnReferral,
  index: number
): string =>
  referral.uuid ||
  referral.user_uuid ||
  referral.smest_id ||
  referral.email ||
  referral.mobile ||
  `ref-${index}`;

const mergeReferrals = (
  baseData: ReferralSummary | null,
  details: ReferralDetail[]
) => {
  const list = baseData?.referrals ?? [];
  if (!list.length) {
    return [];
  }

  const detailsByUuid = new Map(
    details
      .filter((item) => item.user_uuid)
      .map((item) => [item.user_uuid as string, item])
  );
  const detailsByName = new Map(
    details
      .filter((item) => item.referral_name)
      .map((item) => [(item.referral_name || "").trim().toLowerCase(), item])
  );

  return list.map((referral, index) => {
    const primary =
      (referral.uuid && detailsByUuid.get(referral.uuid)) ||
      (referral.user_uuid && detailsByUuid.get(referral.user_uuid));
    const fallback = detailsByName.get(
      (referral.name || "").trim().toLowerCase()
    );
    const merged = {
      ...referral,
      ...(primary || fallback),
    };
    return {
      ...merged,
      rowId: buildReferralRowId(merged, index),
    };
  });
};

const deriveRewardFromReferral = (referral: ReferEarnReferral | null) => {
  if (!referral) return null;
  const candidateKeys = [
    "reward_amount",
    "rewardAmount",
    "reward_value",
    "rewardValue",
    "reward",
  ];
  for (const key of candidateKeys) {
    const value = referral[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

const formatMaybeDate = (value?: string | number | Date | null) => {
  if (!value) return "--";
  const date = moment(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "--";
};

const formatMaybeCurrency = (value?: number) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return formatCurrency(value);
  }
  return "--";
};

const ReferralCard = ({
  referral,
  checked,
  onToggle,
}: {
  referral: ReferEarnReferral;
  checked: boolean;
  onToggle: () => void;
}) => {
  const investmentAmount = formatMaybeCurrency(referral.amount);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: checked ? "primary.main" : "divider",
        bgcolor: checked ? "primary.main" + "10" : "background.paper",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Checkbox checked={checked} onChange={onToggle} />
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {referral.name || referral.referral_name || "Referral"}
            {referral.smest_id ? ` (${referral.smest_id})` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Signup: {formatMaybeDate(referral.user_signup_date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Order: {formatMaybeDate(referral.order_date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Investment Amount: {investmentAmount}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

const OrderCard = ({
  selection,
  checked,
  onToggle,
}: {
  selection: ReferEarnOrderSelection;
  checked: boolean;
  onToggle: () => void;
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderColor: checked ? "primary.main" : "divider",
    }}
  >
    <Stack direction="row" spacing={1.5}>
      <Checkbox checked={checked} onChange={onToggle} />
      <Box flex={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {selection.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selection.subtitle}
        </Typography>
        {selection.orderDate && (
          <Typography variant="body2" color="text.secondary">
            Order Date: {formatMaybeDate(selection.orderDate)}
          </Typography>
        )}
      </Box>
      <Chip
        label={selection.type === "BOND" ? "Bond" : "IPO"}
        size="small"
        color={selection.type === "BOND" ? "primary" : "secondary"}
        sx={{ alignSelf: "flex-start" }}
      />
    </Stack>
  </Paper>
);

export interface ReferEarnInvoiceFieldsProps {
  form: ReferEarnInvoiceForm;
  errors: Record<string, string>;
  onChange: (
    field: keyof ReferEarnInvoiceForm
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFormPatch: (updates: Partial<ReferEarnInvoiceForm>) => void;
  onRegenerateUuid: () => void;
  onApplyDefaultInvoiceNumber: () => void;
  state: ReferEarnState;
  setState: React.Dispatch<React.SetStateAction<ReferEarnState>>;
  calculations: ReferEarnCalculations;
}

export const ReferEarnInvoiceFields = ({
  form,
  errors,
  onChange,
  onFormPatch,
  onRegenerateUuid,
  onApplyDefaultInvoiceNumber,
  state,
  setState,
  calculations,
}: ReferEarnInvoiceFieldsProps) => {
  const [userOpen, setUserOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<readonly UserOption[]>([]);
  const loadingUsers = userOpen && userOptions.length === 0;
  const selectedReferral = useMemo(() => getSelectedReferral(state), [state]);
  const rewardSuggestion = useMemo(
    () => deriveRewardFromReferral(selectedReferral),
    [selectedReferral]
  );
  const totalRewardsClaimed =
    state.referralSummary?.reward_claimed ?? Number.NaN;
  const showUtrField = Boolean(state.selectedOrder);

  useEffect(() => {
    let active = true;
    if (!loadingUsers) return () => undefined;
    makeGETRequestOnUserService("/user/user_list/")
      .then((response) => {
        if (!active) return;
        if (response && response.status === 200) {
          const data = response.data as IUser[];
          const options = data.map((item) => ({
            id: item.uuid,
            name: `${item.firstName} ${item.lastName} - ${item.mobileNumber}`,
          }));
          setUserOptions(options);
        }
      })
      .catch((error) => toastr.error(error));
    return () => {
      active = false;
    };
  }, [loadingUsers]);

  const handleUserChange = (_: any, value: UserOption | null) => {
    if (!value) {
      setState((prev) => ({
        ...createReferEarnState(),
      }));
      onFormPatch({ rewardAmount: "1000", utrNumber: "" });
      return;
    }
    setState(() => ({
      ...createReferEarnState(),
      selectedUser: value,
    }));
    onFormPatch({ rewardAmount: "1000", utrNumber: "" });
  };

  useEffect(() => {
    const userId = state.selectedUser?.id;
    if (!userId) {
      setState((prev) => ({
        ...prev,
        userDetails: null,
        userDetailsLoading: false,
      }));
      return;
    }
    let active = true;
    setState((prev) => ({ ...prev, userDetailsLoading: true }));
    makeGETRequestOnUserService(`user/get/${userId}/`)
      .then((response) => {
        if (!active) return;
        if (response && response.status === 200) {
          setState((prev) => ({
            ...prev,
            userDetails: response.data as UserDetails,
          }));
        } else {
          toastr.error("Failed to fetch user details");
          setState((prev) => ({ ...prev, userDetails: null }));
        }
      })
      .catch(() => {
        if (active) {
          toastr.error("Failed to fetch user details");
          setState((prev) => ({ ...prev, userDetails: null }));
        }
      })
      .finally(() => {
        if (active) {
          setState((prev) => ({ ...prev, userDetailsLoading: false }));
        }
      });
    return () => {
      active = false;
    };
  }, [state.selectedUser?.id, setState]);

  useEffect(() => {
    const userId = state.selectedUser?.id;
    if (!userId) return;
    let active = true;
    setState((prev) => ({
      ...prev,
      referralsLoading: true,
      referralError: undefined,
      referrals: [],
      referralSummary: null,
      selectedReferralId: null,
    }));

    const fetchReferrals = async () => {
      try {
        const listResponse = await makeGETRequestOnUserService(
          `/user/referrals/list/${userId}/`
        );
        if (!active) return;
        if (listResponse.status !== 200) {
          throw new Error(
            typeof listResponse.data === "string"
              ? listResponse.data
              : "Unable to fetch referrals"
          );
        }
        const referralsData = listResponse.data as ReferralSummary;
        let details: ReferralDetail[] = [];
        try {
          const detailsResponse = await makeGETRequestOnUserService(
            `/user/referrals/details/${userId}/`
          );
          if (
            detailsResponse.status === 200 &&
            Array.isArray(detailsResponse.data)
          ) {
            details = detailsResponse.data as ReferralDetail[];
          }
        } catch (detailError: any) {
          toastr.error(
            detailError?.message || "Unable to fetch referral details"
          );
        }
        const merged = mergeReferrals(referralsData, details);
        if (active) {
          setState((prev) => ({
            ...prev,
            referrals: merged,
            referralSummary: referralsData,
            referralsLoading: false,
          }));
        }
      } catch (error: any) {
        if (!active) return;
        const message = error?.message || "Unable to fetch referrals";
        setState((prev) => ({
          ...prev,
          referralsLoading: false,
          referralError: message,
        }));
      }
    };
    fetchReferrals();
    return () => {
      active = false;
    };
  }, [state.selectedUser?.id, setState]);

  useEffect(() => {
    const referralUserUuid = selectedReferral?.user_uuid;
    if (!referralUserUuid) {
      setState((prev) => ({
        ...prev,
        bondOrders: [],
        ipoOrders: [],
        selectedOrder: null,
        orderError: undefined,
        ordersLoading: false,
      }));
      return;
    }
    let active = true;
    setState((prev) => ({
      ...prev,
      ordersLoading: true,
      orderError: undefined,
      bondOrders: [],
      ipoOrders: [],
      selectedOrder: null,
    }));

    const fetchOrders = async () => {
      try {
        const [bondResponse, ipoResponse] = await Promise.all([
          makeGETRequestOnUserService(
            `order_management_v2/orders/history/${referralUserUuid}/`
          ),
          makeGETRequest(`/ipo/order/history/${referralUserUuid}/`),
        ]);
        if (!active) return;
        if (bondResponse.status !== 200) {
          throw new Error("Failed to fetch bond orders");
        }
        if (ipoResponse.status !== 200) {
          throw new Error("Failed to fetch IPO orders");
        }
        setState((prev) => ({
          ...prev,
          bondOrders: bondResponse.data as BondOrderHistoryResponse[],
          ipoOrders: ipoResponse.data as IPOOrderHistoryResponse[],
          ordersLoading: false,
        }));
      } catch (error: any) {
        if (!active) return;
        const message = error?.message || "Unable to fetch orders";
        setState((prev) => ({
          ...prev,
          orderError: message,
          ordersLoading: false,
        }));
      }
    };

    fetchOrders();
    return () => {
      active = false;
    };
  }, [selectedReferral?.user_uuid]);

  useEffect(() => {
    if (
      rewardSuggestion &&
      (!form.rewardAmount || Number(form.rewardAmount) === 0)
    ) {
      onFormPatch({ rewardAmount: String(rewardSuggestion) });
    }
  }, [rewardSuggestion]);

  const handleReferralToggle = (rowId: string) => {
    setState((prev) => ({
      ...prev,
      selectedReferralId: prev.selectedReferralId === rowId ? null : rowId,
    }));
  };

  const handleOrderToggle = (
    type: OrderSelectionType,
    order: BondOrderHistoryResponse | IPOOrderHistoryResponse
  ) => {
    const id = extractOrderIdentifier(type, order);
    const isSameSelection =
      state.selectedOrder &&
      state.selectedOrder.type === type &&
      state.selectedOrder.id === id;

    if (isSameSelection) {
      setState((prev) => ({ ...prev, selectedOrder: null }));
      return;
    }

    const selection: ReferEarnOrderSelection = {
      type,
      id,
      title: extractOrderTitle(type, order),
      subtitle: extractOrderSubtitle(type, order),
      details: order,
      orderDate: extractOrderDate(type, order),
      utrNumber: extractOrderUtr(order),
    };
    setState((prev) => ({
      ...prev,
      selectedOrder: selection,
    }));
    if (selection.utrNumber) {
      onFormPatch({ utrNumber: selection.utrNumber });
    }
  };

  const userAddressLines = useMemo(
    () => buildUserAddressLines(state.userDetails),
    [state.userDetails]
  );

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Typography variant="h6">Document Details</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onRegenerateUuid}>
              Regenerate UUID
            </Button>
            <Button variant="outlined" onClick={onApplyDefaultInvoiceNumber}>
              Apply Default Number
            </Button>
          </Stack>
        </Stack>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Document UUID"
              value={form.documentUuid}
              onChange={onChange("documentUuid")}
              fullWidth
              error={Boolean(errors.documentUuid)}
              helperText={errors.documentUuid}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Document Date"
              type="datetime-local"
              value={form.documentDateTime}
              onChange={onChange("documentDateTime")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.documentDateTime)}
              helperText={errors.documentDateTime}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Invoice Date"
              type="date"
              value={form.invoiceDate}
              onChange={onChange("invoiceDate")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.invoiceDate)}
              helperText={errors.invoiceDate}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Invoice Number"
              value={form.invoiceNumber}
              onChange={onChange("invoiceNumber")}
              fullWidth
              error={Boolean(errors.invoiceNumber)}
              helperText={errors.invoiceNumber}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Select User
        </Typography>
        <Autocomplete
          open={userOpen}
          onOpen={() => setUserOpen(true)}
          onClose={() => setUserOpen(false)}
          options={userOptions}
          getOptionLabel={(option) => option?.name ?? ""}
          loading={loadingUsers}
          value={state.selectedUser}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          ListboxComponent={Listbox as never}
          onChange={handleUserChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search user"
              error={Boolean(errors.selectedUser)}
              helperText={errors.selectedUser}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
        />
        <Box mt={2}>
          {state.userDetailsLoading ? (
            <Skeleton variant="rectangular" height={90} />
          ) : state.userDetails ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {userAddressLines[0]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SMEST ID: {state.userDetails.smestID || "--"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PAN: {state.userDetails.panCard?.panCardNumber || "--"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userAddressLines.slice(1).join(", ")}
              </Typography>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a user to load profile information.
            </Typography>
          )}
          {errors.userDetails && (
            <Typography color="error" variant="caption">
              {errors.userDetails}
            </Typography>
          )}
        </Box>
        {!Number.isNaN(totalRewardsClaimed) && (
          <Box mt={2}>
            <Chip
              color="info"
              label={`Total Rewards Claimed till date: ${formatCurrency(
                totalRewardsClaimed
              )}`}
            />
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">Referral List</Typography>
              {state.referralsLoading && (
                <CircularProgress color="inherit" size={18} />
              )}
            </Stack>
            <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
              {state.referralError && (
                <Alert severity="error">{state.referralError}</Alert>
              )}
              {!state.referralsLoading && !state.referrals.length && (
                <Typography variant="body2" color="text.secondary">
                  No referrals found for this user.
                </Typography>
              )}
              {state.referrals.map((referral) => (
                <ReferralCard
                  key={referral.rowId}
                  referral={referral}
                  checked={state.selectedReferralId === referral.rowId}
                  onToggle={() => handleReferralToggle(referral.rowId)}
                />
              ))}
            </Box>
            {errors.selectedReferral && (
              <Typography color="error" variant="caption">
                {errors.selectedReferral}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">Orders</Typography>
              {state.ordersLoading && (
                <CircularProgress color="inherit" size={18} />
              )}
            </Stack>
            <Stack spacing={1.5} mt={2}>
              {state.orderError && (
                <Alert severity="error">{state.orderError}</Alert>
              )}
              {!state.ordersLoading &&
                !state.bondOrders.length &&
                !state.ipoOrders.length && (
                  <Typography variant="body2" color="text.secondary">
                    No orders found for this user.
                  </Typography>
                )}
              {state.bondOrders
                .sort(
                  (a, b) =>
                    moment(b.insertedAt).valueOf() -
                    moment(a.insertedAt).valueOf()
                )
                .slice(0, 5)
                .map((order) => {
                  const id = extractOrderIdentifier("BOND", order);
                  const isSelected =
                    state.selectedOrder?.type === "BOND" &&
                    state.selectedOrder?.id === id;
                  return (
                    <OrderCard
                      key={`bond-${id}`}
                      selection={{
                        type: "BOND",
                        id,
                        title: extractOrderTitle("BOND", order),
                        subtitle: extractOrderSubtitle("BOND", order),
                        details: order,
                        orderDate: extractOrderDate("BOND", order),
                        utrNumber: extractOrderUtr(order),
                      }}
                      checked={isSelected}
                      onToggle={() => handleOrderToggle("BOND", order)}
                    />
                  );
                })}
              {state.ipoOrders
                .sort(
                  (a, b) =>
                    moment(b.insertedAt).valueOf() -
                    moment(a.insertedAt).valueOf()
                )
                .slice(0, 5)
                .map((order) => {
                  const id = extractOrderIdentifier("IPO", order);
                  const isSelected =
                    state.selectedOrder?.type === "IPO" &&
                    state.selectedOrder?.id === id;
                  return (
                    <OrderCard
                      key={`ipo-${id}`}
                      selection={{
                        type: "IPO",
                        id,
                        title: extractOrderTitle("IPO", order),
                        subtitle: extractOrderSubtitle("IPO", order),
                        details: order,
                        orderDate: extractOrderDate("IPO", order),
                        utrNumber: extractOrderUtr(order),
                      }}
                      checked={isSelected}
                      onToggle={() => handleOrderToggle("IPO", order)}
                    />
                  );
                })}
            </Stack>
            {errors.selectedOrder && (
              <Typography color="error" variant="caption">
                {errors.selectedOrder}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reward & Payment
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Reward Amount (INR)"
              value={form.rewardAmount}
              onChange={onChange("rewardAmount")}
              fullWidth
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              error={Boolean(errors.rewardAmount)}
              helperText={
                errors.rewardAmount ||
                (rewardSuggestion
                  ? `Suggested from referral: ${formatCurrency(
                      rewardSuggestion
                    )}`
                  : undefined)
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Payment Date"
              type="date"
              value={form.paymentDate}
              onChange={onChange("paymentDate")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.paymentDate)}
              helperText={errors.paymentDate}
            />
          </Grid>
          {showUtrField && (
            <Grid item xs={12} md={4}>
              <TextField
                label="UTR Number"
                value={form.utrNumber}
                onChange={onChange("utrNumber")}
                fullWidth
                error={Boolean(errors.utrNumber)}
                helperText={
                  errors.utrNumber ||
                  (state.selectedOrder
                    ? `Prefilled from ${
                        state.selectedOrder.type === "BOND" ? "Bond" : "IPO"
                      } order`
                    : undefined)
                }
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Checkbox
                checked={form.applyTds}
                onChange={(_, checked) => onFormPatch({ applyTds: checked })}
              />
              <Box>
                <Typography fontWeight={600}>Apply TDS</Typography>
                <Typography variant="body2" color="text.secondary">
                  Rate @ {TDS_RATE}%
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "background.default",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Calculation Preview
              </Typography>
              <Typography variant="body2">
                Value:{" "}
                {Number.isFinite(calculations.rewardAmount)
                  ? formatCurrency(calculations.rewardAmount)
                  : "--"}
              </Typography>
              <Typography variant="body2">
                TDS Amount:{" "}
                {Number.isFinite(calculations.tdsAmount)
                  ? formatCurrency(calculations.tdsAmount)
                  : "--"}
              </Typography>
              <Typography variant="body2">
                Net Payable:{" "}
                {Number.isFinite(calculations.totalPayable)
                  ? formatCurrency(calculations.totalPayable)
                  : "--"}
              </Typography>
              <Typography variant="body2">
                Amount in Words: {calculations.amountInWords || "--"}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {selectedReferral && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Selected Referral Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography fontWeight={600}>
                {selectedReferral.name || selectedReferral.referral_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Signup Date:{" "}
                {formatMaybeDate(selectedReferral.user_signup_date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Order Date: {formatMaybeDate(selectedReferral.order_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Investment Amount:{" "}
                {formatMaybeCurrency(selectedReferral.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reward Claimed:{" "}
                {(selectedReferral.reward_claim && "Yes") || "No"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {state.selectedOrder && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Selected Order Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography fontWeight={600}>
                {state.selectedOrder.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {state.selectedOrder.subtitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Order Date: {formatMaybeDate(state.selectedOrder.orderDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                UTR Number:{" "}
                {state.selectedOrder.utrNumber || form.utrNumber || "--"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Stack>
  );
};

export const generateReferEarnInvoicePdf = (
  form: ReferEarnInvoiceForm,
  calculations: ReferEarnCalculations,
  state: ReferEarnState
) => {
  const userDetails = state.userDetails;
  const selectedReferral = getSelectedReferral(state);
  const totalRewardsClaimed = state.referralSummary?.reward_claimed ?? 0;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.addFileToVFS(
    "PlusJakartaSans-Regular.ttf",
    PlusJakartaSansFontBase64String
  );
  doc.addFont("PlusJakartaSans-Regular.ttf", "PlusJakarta", "normal");
  doc.setFont("PlusJakarta", "normal");

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  const primary = { r: 35, g: 38, b: 45 };
  const muted = { r: 107, g: 114, b: 128 };
  const heading = { r: 15, g: 23, b: 42 };
  const labelColor = { r: 125, g: 133, b: 146 };

  let cursorY = PAGE_MARGIN;
  const logoSize = 18;
  doc.addImage(
    `data:image/png;base64,${faviconPngBase64}`,
    "PNG",
    PAGE_MARGIN,
    cursorY - 2,
    logoSize,
    logoSize
  );

  doc.setFontSize(13);
  doc.setTextColor(heading.r, heading.g, heading.b);
  doc.text(COMPANY_DETAILS.name, PAGE_MARGIN + logoSize + 6, cursorY + 3);

  doc.setFontSize(9);
  doc.setTextColor(muted.r, muted.g, muted.b);
  doc.text("ORIGINAL FOR RECIPIENT", pageWidth - PAGE_MARGIN, cursorY, {
    align: "right",
  });

  cursorY += 5;
  const companyLines = [
    ...COMPANY_DETAILS.addressLines,
    `GSTIN: ${COMPANY_DETAILS.gstin}`,
    `PAN: ${COMPANY_DETAILS.pan}`,
    `CIN: ${COMPANY_DETAILS.cin}`,
  ];
  const infoStartX = PAGE_MARGIN + logoSize + 6;
  doc.setTextColor(primary.r, primary.g, primary.b);
  companyLines.forEach((line) => {
    doc.text(line, infoStartX, cursorY + 4);
    cursorY += 4;
  });
  cursorY += 10;

  doc.setFontSize(14);
  doc.setTextColor(heading.r, heading.g, heading.b);
  doc.text("CREDIT NOTE FOR REFER AND EARN PROGRAM", pageWidth / 2, cursorY, {
    align: "center",
  });
  cursorY += 7;
  doc.setFontSize(9);
  doc.setTextColor(muted.r, muted.g, muted.b);
  doc.text("Credit Note Details", pageWidth / 2, cursorY, { align: "center" });
  cursorY += 8;

  doc.setFontSize(9);
  doc.setTextColor(heading.r, heading.g, heading.b);
  doc.text("Document UUID", PAGE_MARGIN, cursorY);
  doc.text("Document Date", pageWidth - PAGE_MARGIN - 60, cursorY);
  cursorY += 5;
  doc.setTextColor(primary.r, primary.g, primary.b);
  doc.text(form.documentUuid, PAGE_MARGIN, cursorY);
  doc.text(
    formatDate(form.documentDateTime, "DD/MM/YYYY HH:mm"),
    pageWidth - PAGE_MARGIN - 60,
    cursorY
  );
  cursorY += 8;

  doc.setTextColor(heading.r, heading.g, heading.b);
  doc.text("To,", PAGE_MARGIN, cursorY);
  doc.text("Credit Note Information", pageWidth - PAGE_MARGIN - 60, cursorY);
  cursorY += 6;

  const userAddressLines = buildUserAddressLines(userDetails);
  const rewardLine = `Total Rewards Claimed till date: ${formatCurrency(
    totalRewardsClaimed
  )}`;
  const addressLines = [
    ...userAddressLines,
    `PAN: ${userDetails?.panCard?.panCardNumber || "Not Available"}`,
    rewardLine,
  ].filter(Boolean);
  let billY = cursorY;
  doc.setFontSize(9.5);
  doc.setTextColor(primary.r, primary.g, primary.b);
  doc.text(addressLines[0] || "--", PAGE_MARGIN, billY);
  billY += 5;
  doc.setFontSize(9);
  doc.setTextColor(muted.r, muted.g, muted.b);
  addressLines.slice(1).forEach((line) => {
    const isRewardLine = line === rewardLine;
    if (isRewardLine) {
      const [label, ...rest] = line.split(":");
      const labelText = `${label.trim()}:`;
      const valueText = rest.join(":").trim();
      const valueGap = 1;
      doc.setTextColor(labelColor.r, labelColor.g, labelColor.b);
      doc.text(labelText, PAGE_MARGIN, billY);
      const labelWidth = doc.getTextWidth(labelText) + valueGap;
      doc.setTextColor(primary.r, primary.g, primary.b);
      doc.text(valueText, PAGE_MARGIN + labelWidth, billY);
      billY += 4;
      doc.setTextColor(muted.r, muted.g, muted.b);
      return;
    }
    const wrapped = doc.splitTextToSize(line, pageWidth / 2 - PAGE_MARGIN);
    wrapped.forEach((segment) => {
      doc.text(segment, PAGE_MARGIN, billY);
      billY += 4;
    });
  });

  const infoLabelX = pageWidth - PAGE_MARGIN - 60;
  const infoValueX = pageWidth - PAGE_MARGIN;
  let infoY = cursorY;
  const invoiceInfo: Array<[string, string]> = [
    ["Date", formatDate(form.invoiceDate, "DD/MM/YYYY")],
    ["Credit Note Number", form.invoiceNumber],
  ];
  invoiceInfo.forEach(([label, value]) => {
    doc.setFontSize(9);
    doc.setTextColor(muted.r, muted.g, muted.b);
    doc.text(`${label}:`, infoLabelX, infoY);
    doc.setTextColor(primary.r, primary.g, primary.b);
    doc.text(value, infoValueX, infoY, { align: "right" });
    infoY += 4.8;
  });

  cursorY = Math.max(billY, infoY) + 8;

  const description = buildReferralDescription(selectedReferral, userDetails);

  const tableWidth = pageWidth - PAGE_MARGIN * 2;
  const valueColumnWidth = 32;
  const descriptionColumnWidth = tableWidth - valueColumnWidth * 3;

  autoTable(doc, {
    startY: cursorY,
    head: [["Description", "Value", `TDS @${TDS_RATE}%`, "Total"]],
    body: [
      [
        description,
        formatCurrency(calculations.rewardAmount),
        formatCurrency(calculations.tdsAmount),
        formatCurrency(calculations.totalPayable),
      ],
      [
        "Total",
        formatCurrency(calculations.rewardAmount),
        formatCurrency(calculations.tdsAmount),
        formatCurrency(calculations.totalPayable),
      ],
    ],
    styles: {
      font: "PlusJakarta",
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      valign: "top",
    },
    headStyles: {
      fillColor: [35, 38, 45],
      textColor: 255,
    },
    columnStyles: {
      0: {
        cellWidth: descriptionColumnWidth,
        halign: "left",
        cellPadding: { top: 4, left: 6, right: 4, bottom: 4 },
      },
      1: {
        cellWidth: valueColumnWidth,
        halign: "left",
        cellPadding: { top: 4, left: 6, right: 4, bottom: 4 },
      },
      2: {
        cellWidth: valueColumnWidth,
        halign: "left",
        cellPadding: { top: 4, left: 6, right: 4, bottom: 4 },
      },
      3: {
        cellWidth: valueColumnWidth,
        halign: "left",
        cellPadding: { top: 4, left: 6, right: 4, bottom: 4 },
      },
    },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    didParseCell: (data) => {
      if (data.section === "head") {
        data.cell.styles.halign = "left";
        data.cell.styles.cellPadding = { top: 5, left: 6, right: 4, bottom: 5 };
      }
      if (data.section === "body" && data.row.index === 1) {
        data.cell.styles.fillColor = [238, 242, 248];
        data.cell.styles.textColor = [primary.r, primary.g, primary.b];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  cursorY = (doc as any).lastAutoTable.finalY + 6;
  doc.setFontSize(9.5);
  const totalWordsLabel = "Total amount in words:";
  doc.setTextColor(labelColor.r, labelColor.g, labelColor.b);
  doc.text(totalWordsLabel, PAGE_MARGIN, cursorY);
  doc.setTextColor(primary.r, primary.g, primary.b);
  const totalWordsValueX = PAGE_MARGIN + doc.getTextWidth(totalWordsLabel) + 1;
  const totalWordsAvailableWidth = Math.max(
    pageWidth - PAGE_MARGIN - totalWordsValueX,
    40
  );
  const totalWordsLines = doc.splitTextToSize(
    calculations.amountInWords || "--",
    totalWordsAvailableWidth
  );
  doc.text(totalWordsLines, totalWordsValueX, cursorY, {
    lineHeightFactor: 1.2,
  });
  cursorY += totalWordsLines.length * 4 + 8;
  doc.setTextColor(primary.r, primary.g, primary.b);

  const acknowledgement =
    "Dear Sir/Madam,\nWe have credited the Referral Bonus to your registered Bank Account with us. The details of the Refer and Earn are mentioned above. Please confirm the receipt of the same by e-mail.";
  acknowledgement.split("\n").forEach((paragraph) => {
    const wrapped = doc.splitTextToSize(paragraph, pageWidth - PAGE_MARGIN * 2);
    wrapped.forEach((line) => {
      doc.text(line, PAGE_MARGIN, cursorY);
      cursorY += 4;
    });
  });
  cursorY += 4;

  doc.setTextColor(labelColor.r, labelColor.g, labelColor.b);
  doc.text("Payment Date", PAGE_MARGIN, cursorY);
  doc.text("UTR Number", pageWidth / 2, cursorY);
  doc.setTextColor(primary.r, primary.g, primary.b);
  cursorY += 5;
  doc.text(formatDate(form.paymentDate, "DD-MM-YYYY"), PAGE_MARGIN, cursorY);
  doc.text(form.utrNumber || "--", pageWidth / 2, cursorY);
  cursorY += 10;

  const disclaimers = [
    "# Discrepancy, if any, noticed in the above Credit Note shall be informed to us within seven days of the receipt of this Credit Note. In case no communication is received within fifteen days, then the same will be treated as accepted by the client.",
    "# As the receiver’s annual aggregate turnover is less than the mandatory threshold limit prescribed under Goods and Services Tax Act, GST component is not part of this Credit Note.",
    "# The receiver of this Credit Note hereby acknowledges to pay all Taxes Applicable on the Amounts Received.",
    "# This is a computer generated credit note and does not require a physical signature.",
    "# The Refer and Earn Program is covered under the Terms and Conditions. The user of the platform hereby agrees to all the Terms and Conditions of the Refer and Earn Program.",
    "# TDS @10% will be applicable once the User crosses ₹20,000 in a given Financial Year.",
  ];
  const disclaimerMaxWidth = pageWidth - PAGE_MARGIN * 2;
  const wrappedDisclaimers = disclaimers.map((line) =>
    doc.splitTextToSize(line, disclaimerMaxWidth)
  );
  const lineHeight = 3.6;
  const disclaimersHeight =
    wrappedDisclaimers.reduce((sum, lines) => sum + lines.length, 0) *
      lineHeight +
    (wrappedDisclaimers.length - 1) * 0.8;
  let disclaimersY = pageHeight - PAGE_MARGIN - disclaimersHeight;
  if (disclaimersY < cursorY + 6) {
    disclaimersY = cursorY + 6;
  }
  doc.setFontSize(7.5);
  doc.setTextColor(muted.r, muted.g, muted.b);
  const firstDisclaimerOffset = 4;
  wrappedDisclaimers.forEach((lines, index) => {
    const offsetY =
      index === 0 ? disclaimersY + firstDisclaimerOffset : disclaimersY;
    doc.text(disclaimers[index], PAGE_MARGIN, offsetY, {
      maxWidth: disclaimerMaxWidth,
      lineHeightFactor: 1.25,
    });
    disclaimersY = offsetY + lines.length * lineHeight;
    if (index < wrappedDisclaimers.length - 1) {
      disclaimersY += 0.8;
    }
    if (index === 0) {
      disclaimersY -= firstDisclaimerOffset;
    }
  });

  const userName =
    `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`.trim() ||
    state.selectedUser?.name ||
    "ReferEarn";
  const fileName = sanitizeFileName(
    `${userName}_ReferEarn_${formatDate(form.invoiceDate, "YYYYMMDD")}.pdf`,
    "ReferEarn_CreditNote.pdf"
  );
  const pdfBlob = doc.output("blob");

  const link = document.createElement("a");
  link.href = URL.createObjectURL(pdfBlob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
  return pdfBlob;
};
